import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ExportConnection, ImportMode, OrganisationSettings, RevisionDateSettingsDto, Ucum } from '@treetop/shared/model';

import { TreetopPropertiesProvider } from './services/treetop-properties-provider';

type ColorPresetDto = {
  id?: string;
  name?: string;
  fill?: string;
  text?: string;
  stroke?: string;
};

type OrganisationCustomisationsDto = {
  colorPresets?: ColorPresetDto[];
  flowchart?: Record<string, string>;
};

class OrganisationSettingsDto {
  revisionDateSettings?: RevisionDateSettingsDto;
  isImportEnabled?: boolean;
  defaultImportMode?: ImportMode;
  isApprovalNoteEnabled?: boolean;
  isReaderFeedbackEnabled?: boolean;
  isPersonalNotesEnabled?: boolean;
  minimumNumberOfReviewers?: number;
  allowOwnerToBypassReviewers?: boolean;
  pdfExportDisclaimer?: string;
  allowUnpublishedPdfExport?: boolean;
  isHtmlExportEnabled?: boolean;
  isTemporaryEditorRoleEnabled?: boolean;
  privacyNoticeUrl?: string;
  isExportToExternalEnabled?: boolean;
  exportConnections?: unknown[];
  allowOfflineSync?: boolean;
  syncAllGuidelines?: boolean;
  syncTaxonomyNodeIds?: string[];
  customisations?: OrganisationCustomisationsDto;
}

@Injectable({ providedIn: 'root' })
export class OrganisationSettingsHttpService {
  private http = inject(HttpClient);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api/organisations/settings`;

  public getOrganisationSettings(): Observable<OrganisationSettings> {
    return this.http
      .get<OrganisationSettingsDto>(`${this.baseUrl}`)
      .pipe(map(OrganisationSettingsHttpService.mapOrganisationSettingsDtoToOrganisationSettings));
  }

  public setOrganisationSettings(settings: OrganisationSettings): Observable<OrganisationSettings> {
    return this.http
      .put<OrganisationSettingsDto>(
        `${this.baseUrl}`,
        OrganisationSettingsHttpService.mapOrganisationSettingsToOrganisationSettingsDto(settings),
      )
      .pipe(map((dto) => OrganisationSettingsHttpService.mapOrganisationSettingsDtoToOrganisationSettings(dto)));
  }

  public getExportConnectionSecret(connectionName: string): Observable<string> {
    return this.http.get(`${this.baseUrl}/export-connections/${encodeURIComponent(connectionName)}/secret`, {
      responseType: 'text',
    });
  }

  public static mapOrganisationSettingsDtoToOrganisationSettings = (dto: OrganisationSettingsDto): OrganisationSettings => {
    if (!dto) {
      return undefined;
    }

    return {
      revisionDateSettings: dto.revisionDateSettings
        ? {
            isRevisionDateEnabled: dto.revisionDateSettings.isRevisionDateEnabled,
            isRevisionDateRequired: dto.revisionDateSettings.isRevisionDateRequired,
            soonToExpirePeriod: dto.revisionDateSettings.soonToExpirePeriod as Ucum,
          }
        : undefined,
      importSettings: {
        isImportEnabled: dto.isImportEnabled ?? false,
        defaultImportMode: dto.defaultImportMode,
      },
      isApprovalNoteEnabled: dto.isApprovalNoteEnabled ?? false,
      isReaderFeedbackEnabled: dto.isReaderFeedbackEnabled ?? true,
      isPersonalNotesEnabled: dto.isPersonalNotesEnabled ?? true,
      isTemporaryEditorRoleEnabled: dto.isTemporaryEditorRoleEnabled ?? false,
      privacyNoticeUrl: dto.privacyNoticeUrl ?? undefined,
      reviewSettings: {
        minimumNumberOfReviewers: dto.minimumNumberOfReviewers ?? 0,
        allowOwnerToBypassReviewers: dto.allowOwnerToBypassReviewers ?? false,
      },
      exportSettings: {
        pdfExportDisclaimer: dto.pdfExportDisclaimer ?? undefined,
        allowUnpublishedPdfExport: dto.allowUnpublishedPdfExport ?? false,
        htmlExportEnabled: dto.isHtmlExportEnabled ?? false,
        externalExportEnabled: dto.isExportToExternalEnabled ?? false,
        externalExportConnections: (dto.exportConnections ?? []) as ExportConnection[],
      },
      offlineSettings: {
        allowOfflineSync: dto.allowOfflineSync ?? false,
        syncAllGuidelines: dto.syncAllGuidelines ?? false,
        syncTaxonomyNodeIds: dto.syncTaxonomyNodeIds ?? [],
      },
      customisations: {
        colorPresets: (dto.customisations?.colorPresets ?? []).filter(
          (preset): preset is NonNullable<OrganisationSettings['customisations']>['colorPresets'][number] =>
            Boolean(preset.id && preset.name),
        ),
        flowchart: dto.customisations?.flowchart ?? {},
      },
    };
  };

  public static mapOrganisationSettingsToOrganisationSettingsDto = (settings: OrganisationSettings): OrganisationSettingsDto => ({
    revisionDateSettings: settings.revisionDateSettings
      ? {
          isRevisionDateEnabled: settings.revisionDateSettings.isRevisionDateEnabled,
          isRevisionDateRequired: settings.revisionDateSettings.isRevisionDateRequired,
          soonToExpirePeriod: settings.revisionDateSettings.soonToExpirePeriod,
        }
      : undefined,
    isImportEnabled: settings.importSettings?.isImportEnabled,
    defaultImportMode: settings.importSettings?.defaultImportMode,
    isApprovalNoteEnabled: settings.isApprovalNoteEnabled,
    isReaderFeedbackEnabled: settings.isReaderFeedbackEnabled,
    isPersonalNotesEnabled: settings.isPersonalNotesEnabled,
    isTemporaryEditorRoleEnabled: settings.isTemporaryEditorRoleEnabled,
    privacyNoticeUrl: settings.privacyNoticeUrl,
    minimumNumberOfReviewers: settings.reviewSettings?.minimumNumberOfReviewers,
    allowOwnerToBypassReviewers: settings.reviewSettings?.allowOwnerToBypassReviewers,
    pdfExportDisclaimer: settings.exportSettings.pdfExportDisclaimer,
    allowUnpublishedPdfExport: settings.exportSettings.allowUnpublishedPdfExport,
    isHtmlExportEnabled: settings.exportSettings.htmlExportEnabled,
    isExportToExternalEnabled: settings.exportSettings.externalExportEnabled,
    exportConnections: settings.exportSettings.externalExportConnections,
    allowOfflineSync: settings.offlineSettings.allowOfflineSync,
    syncAllGuidelines: settings.offlineSettings.syncAllGuidelines,
    syncTaxonomyNodeIds: settings.offlineSettings.syncTaxonomyNodeIds,
    customisations: settings.customisations,
  });
}
